"use client";

import { Heading, Icon, Image, Link, Text } from "@pamperedchef/design-system";

import { FeaturedText, RichText } from "@/composites/foundation";
import { ResponsiveImage } from "@/composites/landing/ResponsiveImage";
import type { CmsImage } from "@/composites/landing/types";
import * as styles from "./ClaimOutcome.module.scss";

export interface ClaimOutcomeProps {
	/** "success" or "ineligible"; surfaces as `data-outcome` for tests and styling. */
	outcome: "success" | "ineligible";
	iconImage?: CmsImage;
	heading: string;
	/** HTML, placeholders already filled. */
	copy?: string;
	followupHeading?: string;
	/** HTML, placeholders already filled. */
	followupCopy?: string;
	consultantPhone?: string;
	consultantEmail?: string;
	desktopImage?: CmsImage;
	mobileImage?: CmsImage;
}

/**
 * Replaces the landing once the claim outcome is known. Success and ineligible
 * share the layout; only the copy differs.
 */
export function ClaimOutcome({
	outcome,
	iconImage,
	heading,
	copy,
	followupHeading,
	followupCopy,
	consultantPhone,
	consultantEmail,
	desktopImage,
	mobileImage
}: ClaimOutcomeProps) {
	const hasContact = Boolean(consultantPhone || consultantEmail);
	const hasImage = Boolean(desktopImage || mobileImage);

	return (
		<section
			className={styles["claim-outcome"]}
			data-component="ClaimOutcome"
			data-outcome={outcome}
		>
			{hasImage ? (
				<div className={styles["claim-outcome__media"]}>
					<ResponsiveImage
						desktop={desktopImage}
						mobile={mobileImage}
					/>
				</div>
			) : null}

			<div className={styles["claim-outcome__panel"]}>
				{iconImage ? (
					<span
						className={styles["claim-outcome__icon"]}
						aria-hidden="true"
					>
						<Image src={iconImage.url} alt="" objectFit="contain" />
					</span>
				) : null}

				<FeaturedText
					className={styles["claim-outcome__heading"]}
					headline={heading}
					styledAs="display-sm"
					alignment="center"
					headingTag="h1"
				/>

				{copy ? (
					<RichText
						content={copy}
						className={styles["claim-outcome__copy"]}
					/>
				) : null}

				{followupHeading || followupCopy || hasContact ? (
					<div className={styles["claim-outcome__more"]}>
						{followupHeading ? (
							<Heading
								tag="h2"
								className={
									styles["claim-outcome__more-heading"]
								}
							>
								{followupHeading}
							</Heading>
						) : null}
						{followupCopy ? (
							<RichText
								content={followupCopy}
								className={styles["claim-outcome__copy"]}
							/>
						) : null}

						{hasContact ? (
							<ul className={styles["claim-outcome__contact"]}>
								{consultantPhone ? (
									<li
										className={
											styles[
												"claim-outcome__contact-item"
											]
										}
									>
										<Icon name="phone-call" size={20} />
										<Text tag="span" styledAs="smr">
											{consultantPhone}
										</Text>
									</li>
								) : null}
								{consultantEmail ? (
									<li
										className={
											styles[
												"claim-outcome__contact-item"
											]
										}
									>
										<Icon name="mail" size={20} />
										<Link
											href={`mailto:${consultantEmail}`}
											size="sm"
										>
											{consultantEmail}
										</Link>
									</li>
								) : null}
							</ul>
						) : null}
					</div>
				) : null}
			</div>
		</section>
	);
}
